import {
	CreateInsurerDto,
	GetInsurersQueryParams,
	InsurerWithSociety,
	PaginatedResponse,
	UpdateInsurerDto,
} from "@dune/types";
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { privateAxios } from "../axios";

const insurersQueryKeys = {
	all: ["insurers"] as const,
	lists: () => [...insurersQueryKeys.all, "list"] as const,
	list: (page: number, limit: number, search?: string) =>
		[...insurersQueryKeys.lists(), { page, limit, search }] as const,
};

const getAllInsurers = async (
	params: GetInsurersQueryParams,
): Promise<PaginatedResponse<InsurerWithSociety>> => {
	const { data } = await privateAxios.get<
		PaginatedResponse<InsurerWithSociety>
	>("/insurers/all", {
		params: { page: params.page, limit: params.limit, search: params.search },
	});
	return data;
};

export const useGetAllInsurers = (params: GetInsurersQueryParams = {}) => {
	const page = params.page ?? 1;
	const limit = params.limit ?? 10;
	const search = params.search ?? "";
	return useQuery({
		queryKey: insurersQueryKeys.list(page, limit, search),
		queryFn: () => getAllInsurers({ page, limit, search }),
		placeholderData: keepPreviousData,
	});
};

const createInsurer = async (
	payload: CreateInsurerDto,
): Promise<InsurerWithSociety> => {
	const { data } = await privateAxios.post<InsurerWithSociety>(
		"/insurers",
		payload,
	);
	return data;
};

export const useCreateInsurer = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createInsurer,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: insurersQueryKeys.lists() });
		},
	});
};

const updateInsurer = async (
	id: number,
	payload: UpdateInsurerDto,
): Promise<InsurerWithSociety> => {
	const { data } = await privateAxios.patch<InsurerWithSociety>(
		`/insurers/${id}`,
		payload,
	);
	return data;
};

export const useUpdateInsurer = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, payload }: { id: number; payload: UpdateInsurerDto }) =>
			updateInsurer(id, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: insurersQueryKeys.lists() });
		},
	});
};

const deleteInsurer = async (id: number): Promise<void> => {
	await privateAxios.delete(`/insurers/${id}`);
};

export const useDeleteInsurer = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteInsurer,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: insurersQueryKeys.lists() });
		},
	});
};
